import type { LiveFigures } from './content';
import { resolveDerivations } from '../api/campaignData';
import { evaluateDerivation } from '../composition/derive/evaluate';

/**
 * Per-gap-row live-figure functions, keyed by the row `slug`.
 *
 * A gap row's live figure is *behavior*, not content: it reads the account's live insights and/or the
 * uploaded install base and derives a one-line current-state string (or null when there is no live
 * figure for that row, so the SE-authored `currentState` shows instead). `resolveGapRows` marries each
 * DB `gap_row` block to the matching function here by `slug`, falling back to `() => null` for a slug
 * with no registered function.
 *
 * The SINGLE-value figures are DECLARATIVE: their logic is the campaign's seeded
 * `live_figure_derivation` {@link Derivation} data, and this file just runs them through
 * `evaluateDerivation`. A derivation that renders nothing (its source is absent), or a slug the campaign
 * has not seeded a derivation for, maps back to `null` so the SE-authored `currentState` shows — the
 * original null-defer behaviour. The COMPOUND figures (`eol`/`security`) branch install-base-vs-insights
 * in a way a single derivation cannot express, so they stay hand-written here (candidates for the
 * behavior registry); `dataplatform` has no live figure at all.
 */

/**
 * Run a campaign's seeded live-figure derivation, deferring to the SE-authored text (null) when it's
 * empty or unseeded. The derivation is looked up at CALL time (not module load) so it reads the
 * campaign's seeded `live_figure_derivation` block — the DB is the sole source, there is no bundled
 * fallback. The lazy read also keeps this cycle-free and safe before the runner data is injected.
 */
function derived(slug: string): (live: LiveFigures) => string | null {
  return ({ insights, installBase }) => {
    const derivation = resolveDerivations('live_figure_derivation')[slug];
    if (!derivation) return null;
    return evaluateDerivation(derivation, { insights, installBase }) || null;
  };
}

export const LIVE_CURRENT_STATE_BY_SLUG: Record<string, (live: LiveFigures) => string | null> = {
  compute: derived('compute'),

  // The install base was counted from the customer's own export, so it wins. Compound (a threshold gate
  // plus an optional trailing clause, then an insights fallback) — kept hand-written.
  eol: ({ insights, installBase }) => {
    const life = installBase?.lifecycle;
    if (life?.counts && life.counts.pastSupport > 0) {
      const soon = life.counts.endingSoon;
      return (
        `${life.counts.pastSupport} of ${life.totalSubCategories} sub-categories past end of support` +
        (soon > 0 ? `, ${soon} more within 12 months` : '')
      );
    }
    return insights?.eolDeviceCount === undefined
      ? null
      : `${insights.eolDeviceCount} devices past end-of-support`;
  },

  security: ({ insights, installBase }) => {
    const life = installBase?.lifecycle;
    if (life?.counts && life.counts.noSecurityFixes > 0) {
      return `${life.counts.noSecurityFixes} of ${life.totalSubCategories} sub-categories past security support`;
    }
    return insights?.openCriticalAdvisories === undefined
      ? null
      : `${insights.openCriticalAdvisories} open critical advisories`;
  },

  licensing: derived('licensing'),

  network: derived('network'),

  // No Ready/IQ field maps to this one; it stays SE-authored.
  dataplatform: () => null,
};
